import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import NotificationBell from "@/features/notifications/NotificationBell";
import HeaderSearch from "@/features/search/HeaderSearch";
import { App_Url_New } from "@/shared/common/static";
import { BackButton, IconButton, LinkButton } from "@/shared/components";
import CommonLogoutModal from "@/shared/components/CommonLogoutModal";
import { getRouteTitle } from "@/shared/lib/routeMeta";
import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { openModal } from "@/shared/redux/reducers/uiSlice";
import { useAppSelector } from "@/shared/redux/store/hooks";

const getInitials = (fullName?: string | null) => {
  if (!fullName) return "U";
  const names = fullName.trim().split(" ").filter(Boolean);
  if (names.length === 0) return "U";
  if (names.length === 1) return names[0][0].toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
};

export default function DashboardHeader(): React.JSX.Element {
  const pageShellClasses = getThemeComponentGroup("pageShell");
  const cardClasses = getThemeComponentGroup("card");
  const user = useAppSelector((state) => state.auth.user);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const title = getRouteTitle(pathname, search);
  const userName = getInitials(user?.user?.full_name);

  const handleSignOut = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.LOGOUT_CONFIRM,
        title: "Logout?",
        className: "max-w-md",
        children: <CommonLogoutModal displayName={userName} />,
      })
    );
  };

  return (
    <header
      className={`h-16 border-b flex items-center justify-between px-4 md:px-8 sticky top-0 z-30 ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")} ${getThemeClass(pageShellClasses.borderColor, "border-theme-border/70")}`}
    >
      <div className="flex items-center gap-2">
        <BackButton variant="icon" />
        <h1
          className={`text-lg font-semibold ${getThemeClass(pageShellClasses.textColor, "text-theme-text")}`}
        >
          {title}
        </h1>
      </div>

      <div className="flex items-center gap-4 md:gap-8">
        <HeaderSearch />
        <div className="flex items-center gap-3">
          <NotificationBell />
          <UserAvatarDropdown
            userName={userName}
            menuItems={[
              {
                label: "Account security",
                onClick: () => navigate(App_Url_New.secured.frontlinks.accountSecurity),
              },
              { label: "Logout", danger: true, onClick: handleSignOut },
            ]}
          />
        </div>
      </div>
    </header>
  );
}

type MenuItem = { label: string; onClick?: () => void; danger?: boolean };

const UserAvatarDropdown = ({
  userName = "U",
  menuItems = [],
}: {
  userName?: string;
  menuItems?: MenuItem[];
}) => {
  const avatarClasses = getThemeComponentGroup("avatar");
  const menuClasses = getThemeComponentGroup("menu");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <IconButton
        aria-label="Account menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        size="sm"
        className={`h-8 w-8 text-xs font-bold select-none ${getThemeClass(avatarClasses.badgeBgColor, "bg-theme-primary-alt")} ${getThemeClass(avatarClasses.badgeTextColor, "text-theme-text-inverse")}`}
        icon={<span>{userName}</span>}
      />

      {open && (
        <div
          className={`absolute right-0 mt-2 w-44 shadow-lg rounded-md border z-50 ${getThemeClass(menuClasses.panelBgColor, "bg-theme-surface")} ${getThemeClass(menuClasses.panelBorderColor, "border-theme-border")}`}
        >
          <ul
            className={`text-sm ${getThemeClass(menuClasses.itemTextColor, "text-theme-text-muted")}`}
          >
            {menuItems.map((item) => (
              <li key={item.label}>
                <LinkButton
                  variant="menu"
                  onClick={() => {
                    item.onClick?.();
                    setOpen(false);
                  }}
                  className={`${getThemeClass(menuClasses.itemHoverBgColor, "hover:bg-theme-surface-soft")} ${
                    item.danger ? "text-theme-danger hover:bg-theme-danger-bright-soft" : ""
                  }`}
                >
                  {item.label}
                </LinkButton>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
